/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  MONTH_NAME_LIST_MLS,
  MONTH_SHORT_NAME_LIST_MLS,
  REPORT_FRANCHISE_MONTHLY,
  ReportService,
  salesManagementReportFilterDialogRoute,
  whenFormInvalid,
} from '@gh/builder-shared';
import { AelEvents, FranchiseReportFilterDto, reportField } from '@gh/builder-shared-data';
import { ActionFlow, AppRouter, getRouteInitData } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { filter$ } from '@gh/rx/operators';

import { find, findIndex, first, map, omit, padStart, values } from 'lodash';

import { FranchiseMonthlyReportBusinessService } from '../../resources/franchise-monthly-report-business.service';
import {
  decomposeMonthCode,
  FranchiseReportFilter,
  FranchiseReportFilterList,
  FranchiseReportMonthSelectOption,
} from '../../resources/franchise-monthly-report.model';
import { FranchiseReportFilterFormState } from './franchise-report-filter-form.service';
import { Validators } from '@angular/forms';

@Component({
  standalone: false,
  selector: 'gh-franchise-report-filter',
  templateUrl: 'franchise-report-filter.component.html',
  styleUrls: ['franchise-report-filter.component.scss'],
  providers: [
    FranchiseReportFilterFormState,
  ],
})
export class FranchiseReportFilterComponent extends AbstractFormComponent<FranchiseReportFilter>
  implements OnInit {
  currentMonthHistory?: FranchiseReportFilterDto;
  originalReportFilter: FranchiseReportFilterList;
  monthSelectOptionList: FranchiseReportMonthSelectOption[];

  constructor(public form: FranchiseReportFilterFormState,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private waitingService: WaitingService,
    private reportService: ReportService,
    private notificationService: NotificationService,
    private navigationService: BuilderNavigationService,
    private router: AppRouter,
    private userContext: BuilderUserContext,
    private businessService: FranchiseMonthlyReportBusinessService,
    private translationService: TranslationService,
    private pipelines: FormActionPipelines,
    private commonActions: CommonActions) {
    super();
  }

  get completeFlow(): ActionFlow<FormFlowState<FranchiseReportFilterList>> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(whenFormInvalid, () => this.handleErrors()),
      execute: this.commonActions.execute(({ value }) =>
        this.businessService.update(this.composeFilterSubmitFormValue(value))),
      finish: ({ value }) => {
        this.updateMonthData(this.getMonthReportFilter(this.form.getMonthCodeValue(), value));
        this.form.build(this.composeBuildFormValue(value));
        this.onWhsChange();
        this.onShowReport();
      },
    });
  }

  ngOnInit(): void {
    const { reportFilter } = getRouteInitData(this.route);
    this.originalReportFilter = reportFilter;
    this.monthSelectOptionList =
      values(map(this.originalReportFilter.historySettings, this.composeMonthSelectOption.bind(this)));
    this.form.build(<FranchiseReportFilter>first(this.originalReportFilter.historySettings));
    this.currentMonthHistory = this.originalReportFilter.historySettings[0];
    this.form.getControl('whs').setValue(this.currentMonthHistory.whs);
    this.form.getControl('whsDetails').setValue(this.currentMonthHistory.whsDetails);
    this.onWhsChange();
    this.form.getControl('monthCode').markAsDirty();
  }

  onReset(): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.build(this.originalReportFilter));
  }

  onMonthChange({ value }: MatSelectChange): void {
    this.form.markAsPristine();
    this.currentMonthHistory = this.getMonthReportFilter(value, this.originalReportFilter);
    this.form.build(this.currentMonthHistory);
    this.form.getControl('whs').setValue(this.currentMonthHistory.whs);
    this.form.getControl('whsDetails').setValue(this.currentMonthHistory.whsDetails);
    this.form.getControl('monthCode').markAsDirty();
  }

  goToAel(): void {
    this.navigationService.goToAel({
      label: 'lbl_franchise_monthly',
      entityId: this.userContext.office!.id,
      entityType: 'OFFICE',
      eventTypeIds: [AelEvents.Office.ProducedFranchiseMonthly],
    });
  }

  goToSalesManagementReport(): void {
    this.dialogService.openWithInput(salesManagementReportFilterDialogRoute, {
      forYear: this.form.getForYearValue(),
      forMonth: this.form.getForMonthValue(),
    });
  }

  onShowReport(): void {
    this.reportService.generateUrlAndShowDialog(REPORT_FRANCHISE_MONTHLY, [
      reportField('ForTheMonthOf', this.getReportFieldValue('ForTheMonthOf')),
      reportField('ApprovalsInArea', this.getReportFieldValue('ApprovalsInArea')),
      reportField('Traffic', this.getReportFieldValue('Traffic')),
      reportField('StaffCount', this.getReportFieldValue('StaffCount')),
      reportField('StaffHours', this.getReportFieldValue('StaffHours')),
      reportField('FinalizedJobs', this.getReportFieldValue('FinalizedJobs')),
      reportField('ConstVariance', this.getReportFieldValue('ConstVariance')),
      reportField('whs', this.getReportFieldValue('whs')),
      reportField('whsDetails', this.getReportFieldValue('whsDetails')),
      reportField('Memo', this.getReportFieldValue('Memo')),
    ], {
      title: 'lbl_link_franchise_monthly',
      linkText: 'lbl_fm_link_text',
    }).subscribe();
  }

  onWhsChange(): void {
    if (this.form.getWhs()) {
      this.form.getControl('whsDetails').setValidators(Validators.required);
    } else {
      this.form.getControl('whsDetails').clearValidators();
      this.form.getControl('whsDetails').updateValueAndValidity();
    }
    if (this.currentMonthHistory) {
      this.currentMonthHistory.whs = this.form.getWhs();
    }
  }

  onWhsDetailsChange(): void {
    if (this.currentMonthHistory) {
      this.currentMonthHistory.whsDetails = this.form.getWhsDetails();
    }
  }

  private handleErrors(): void {
    this.form.get('whsDetails').markAsTouched();
    this.notificationService.error('msg_eom_whs_empty');
  }

  private composeFilterSubmitFormValue(reportFilter: FranchiseReportFilter): FranchiseReportFilterList {
    return {
      ...reportFilter,
      historySettings: this.originalReportFilter.historySettings,
    };
  }

  private composeBuildFormValue(filterList: FranchiseReportFilterList): FranchiseReportFilter {
    return this.getMonthReportFilter(this.form.getMonthCodeValue(), filterList);
  }

  private getMonthReportFilter(monthCode: string, filterList: FranchiseReportFilterList): FranchiseReportFilter {
    const { forYear, forMonth } = decomposeMonthCode(monthCode);
    return find(filterList.historySettings, { forYear, forMonth })
      || <FranchiseReportFilter>omit(filterList, ['historySettings']);
  }

  private composeMonthSelectOption({ monthCode, forMonth, forYear }: FranchiseReportFilterList): FranchiseReportMonthSelectOption {
    return {
      monthCode,
      name: `${this.translationService.get(MONTH_SHORT_NAME_LIST_MLS[forMonth])} ${forYear}`,
    };
  }

  private reportEndpointDate(year: number, month: number, date: number = 1): string {
    return `${year}-${month}-${padStart(
      date.toString(),
      2,
      '0')};${this.translationService.get(MONTH_NAME_LIST_MLS[month])} ${year};${year};${month}`;
  }

  private getReportFieldValue(name: string): string | number {
// @ts-expect-error Legacy typing suppression
    return {
      ForTheMonthOf: this.reportEndpointDate(this.form.getForYearValue(), this.form.getForMonthValue()),
      ApprovalsInArea: this.form.getAllApprovalsValue(),
      Traffic: this.form.getTrafficValue(),
      StaffCount: this.form.getNoOfStuffValue(),
      StaffHours: this.form.getStaffHoursPaidValue(),
      FinalizedJobs: this.form.getFinalizedJobsValue(),
      ConstVariance: this.form.getConstVarianceValue(),
      Memo: this.form.getMemoValue(),
    }[name];
  }

  private updateMonthData(setting: FranchiseReportFilter): void {
    const id = setting.id;
    this.originalReportFilter.historySettings[
      findIndex(this.originalReportFilter.historySettings, { id })
    ] = setting;
  }
}
